import type { Environment } from '../../environment';
import type {
  EntityObject, OverreactRequestShape,
} from '../../public-types';
import { createDataNode, getDataNode } from '../../store';
import { OVERREACT_ID_FIELD_NAME } from '../../store/consts';
import type { ResponseContract } from '../response-contract';
import { responseTypes } from '../response-types';
import { getSideEffectCacheStoreHelpers } from './sideEffectFnHelper';

export default function handler<
  TVariables extends object,
  TData,
  TEntity extends EntityObject,
  TResult extends TEntity | TEntity[] | null,
>(
  environment: Environment,
  processedResponse: TResult,
  request: OverreactRequestShape<TVariables, TData, TResult, TEntity>,
) {
  return (context: ResponseContract<TVariables, TData, TEntity, unknown, TResult>) => {
    const locator = 'locator' in request.variables ? request.variables.locator : undefined;
    if (!locator || typeof locator !== 'object') {
      throw new Error('Request variables must include a locator');
    }
    const { descriptor, order = [] } = locator as {
      descriptor: Record<string, string | number>;
      order: string[];
    };
    const { id: dataRefId, dataCb, spec } = request;
    const { store } = environment;
    const dataNode = getDataNode(context.schemaNode) || createDataNode(context.schemaNode, store);
    const dataRef = dataNode.getDataRef(dataRefId);
    dataRef.clear();
    const { requestContract, sideEffectFn } = spec;
    let parentId = requestContract.parentKeySelector?.(request.variables);

    if (context.responseType === responseTypes.ENTITY) {
      if (!parentId && order.length > 1) {
        parentId = descriptor[order[order.length - 2]];
      }
      const storedEntity = context.applyId(processedResponse as TEntity, parentId);
      dataRef.add(storedEntity[OVERREACT_ID_FIELD_NAME]);
      store.getRecordGroup(context.schemaNode.modelName as string)
        .addOrUpdateRecords([storedEntity], request);
      dataCb?.(processedResponse, request);
    } else if (context.responseType === responseTypes.COLL) {
      if (!parentId && order.length > 0) {
        parentId = descriptor[order[order.length - 1]];
      }
      const storedEntities = (processedResponse as TEntity[]).map(entity => {
        const storedEntity = context.applyId(entity, parentId);
        dataRef.add(storedEntity[OVERREACT_ID_FIELD_NAME]);
        return storedEntity;
      });
      store.getRecordGroup(context.schemaNode.modelName as string)
        .addOrUpdateRecords(storedEntities, request);
      if (storedEntities.length === 0) {
        dataRef.notify('update', [], request);
      }
      dataCb?.(processedResponse, request);
    }

    if (sideEffectFn) {
      sideEffectFn(processedResponse, request, spec, getSideEffectCacheStoreHelpers(environment));
    }
  };
}
